import {
	Copy,
	LayoutDashboard,
	LogOut,
	Monitor,
	Moon,
	PanelLeft,
	RefreshCw,
	Sun,
	UserCircle,
} from "lucide-react"
import { toast } from "sonner"
import { ENVIRONMENTS, getNavigation } from "@/config/navigation"
import type { PaletteCommand } from "../types"

export function environmentTargetPath(
	pathname: string,
	navHrefs: string[],
): string {
	let best = "/"
	for (const href of navHrefs) {
		if (href === "/") continue
		if (
			(pathname === href || pathname.startsWith(`${href}/`)) &&
			href.length > best.length
		) {
			best = href
		}
	}
	return pathname === best || best !== "/" ? best : "/"
}

export interface SwitchCommandDeps {
	setTheme: (theme: "light" | "dark" | "system") => void
	toggleSidebar: () => void
	signOut: () => void
	openUserProfile: () => void
}

export function buildSwitchCommands(deps: SwitchCommandDeps): PaletteCommand[] {
	const themes = [
		{ id: "light", label: "Theme › Light", icon: Sun },
		{ id: "dark", label: "Theme › Dark", icon: Moon },
		{ id: "system", label: "Theme › System", icon: Monitor },
	] as const

	const themeCommands = themes.map(
		({ id, label, icon }): PaletteCommand => ({
			id: `switch:theme:${id}`,
			label,
			icon,
			group: "switch",
			keywords: ["theme", "appearance", "dark mode"],
			perform: (ctx) => {
				deps.setTheme(id)
				ctx.close()
			},
		}),
	)

	const currentOrigin =
		typeof window !== "undefined" ? window.location.origin : ""
	const environmentCommands = ENVIRONMENTS.filter(
		(environment) => environment.href !== currentOrigin,
	).map(
		(environment): PaletteCommand => ({
			id: `switch:environment:${environment.value}`,
			label: `Environment › ${environment.name}`,
			description: environment.href,
			icon: environment.icon,
			group: "switch",
			keywords: ["environment", "env", environment.value],
			perform: () => {
				const navHrefs = getNavigation().map((item) => item.href)
				const path = environmentTargetPath(window.location.pathname, navHrefs)
				window.location.href = `${environment.href}${path === "/" ? "" : path}`
			},
		}),
	)

	return [
		...themeCommands,
		...environmentCommands,
		{
			id: "switch:sidebar",
			label: "Toggle sidebar",
			icon: PanelLeft,
			group: "switch",
			shortcutHint: "⌘B",
			perform: (ctx) => {
				deps.toggleSidebar()
				ctx.close()
			},
		},
		{
			id: "switch:copy-url",
			label: "Copy current URL",
			icon: Copy,
			group: "switch",
			perform: async (ctx) => {
				try {
					await navigator.clipboard.writeText(window.location.href)
					toast.success("URL copied")
				} catch {
					toast.error("Could not copy to clipboard")
				}
				ctx.close()
			},
		},
		{
			id: "switch:refresh-data",
			label: "Refresh all data",
			icon: RefreshCw,
			group: "switch",
			keywords: ["invalidate", "reload"],
			perform: (ctx) => {
				ctx.queryClient.invalidateQueries()
				toast.success("Refreshing data")
				ctx.close()
			},
		},
		{
			id: "switch:dashboard",
			label: "Go to Dashboard",
			icon: LayoutDashboard,
			group: "switch",
			perform: (ctx) => {
				ctx.navigate({ to: "/" })
				ctx.close()
			},
		},
		{
			id: "switch:manage-account",
			label: "Manage account",
			icon: UserCircle,
			group: "switch",
			perform: (ctx) => {
				deps.openUserProfile()
				ctx.close()
			},
		},
		{
			id: "switch:sign-out",
			label: "Sign out",
			icon: LogOut,
			group: "switch",
			perform: () => {
				deps.signOut()
			},
		},
	]
}
